import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import API from "../api/axios";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

export default function JoinTeam() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({ full_name: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!token) { setInvalid(true); return; }
    API.get(`/team/invite-info?token=${token}`)
      .then(res => setInviteInfo(res.data))
      .catch(() => setInvalid(true));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.password || form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await API.post("/team/join", {
        token,
        full_name: form.full_name,
        password: form.password,
      });
      login(res.data);
      toast.success("Welcome to the team!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join team");
    } finally {
      setLoading(false);
    }
  };

  if (invalid) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={{ fontSize: "48px", textAlign: "center" }}>❌</p>
          <h2 style={s.title}>Invalid Invitation</h2>
          <p style={s.sub}>This link is invalid or has already been used.</p>
          <a href="/login" style={s.link}>Go to Login</a>
        </div>
      </div>
    );
  }

  if (!inviteInfo) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={{ color: "#5a8a8a", textAlign: "center" }}>
            Loading invitation...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoRow}>
  <div style={{
    width: "36px", height: "36px",
    background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
    borderRadius: "8px", display: "flex",
    alignItems: "center", justifyContent: "center",
    fontSize: "18px", flexShrink: 0,
  }}>
    📦
  </div>
  <span style={s.logoText}>MartSense</span>
</div>

        <h2 style={s.title}>Accept Invitation</h2>
        <p style={s.sub}>
          You have been invited to join{" "}
          <strong style={{ color: "#2ec4b6" }}>
            {inviteInfo.business_name}
          </strong>{" "}
          as <strong>{inviteInfo.role}</strong>.
        </p>
        <p style={{ ...s.sub, marginBottom: "24px" }}>
          Account email: <strong>{inviteInfo.email}</strong>
        </p>

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>Your Full Name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={e => setForm({ ...form, full_name: e.target.value })}
              placeholder="Enter your name"
              style={s.input}
              required
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>Create Password</label>
            <div style={s.passwordRow}>
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Minimum 6 characters"
                style={{ ...s.input, paddingRight: "48px" }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={s.eyeBtn}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="#5a8a8a" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8
                      a18.45 18.45 0 015.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8
                      a18.5 18.5 0 01-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="#5a8a8a" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={s.submitBtn}
          >
            {loading ? "Creating Account..." : "Join Team →"}
          </button>
        </form>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh", background: "#0a0f0f",
    display: "flex", alignItems: "center",
    justifyContent: "center", padding: "20px",
  },
  card: {
    background: "#0f1a1a", border: "1px solid #1e3333",
    borderRadius: "16px", padding: "40px 32px",
    width: "100%", maxWidth: "420px",
  },
  logoRow: {
    display: "flex", alignItems: "center", gap: "10px",
    justifyContent: "center", marginBottom: "28px",
  },
  logoText: {
    fontSize: "20px", fontWeight: "700", color: "#2ec4b6",
  },
  title: {
    fontSize: "22px", fontWeight: "700", color: "#e8f5f5",
    marginBottom: "8px", textAlign: "center",
  },
  sub: {
    fontSize: "14px", color: "#5a8a8a", lineHeight: "1.6",
    textAlign: "center", marginBottom: "8px",
  },
  form: { display: "flex", flexDirection: "column", gap: "16px" },
  field: { display: "flex", flexDirection: "column", gap: "8px" },
  label: { fontSize: "13px", fontWeight: "500", color: "#5a8a8a" },
  input: {
    background: "#162424", border: "1px solid #1e3333",
    borderRadius: "10px", padding: "12px 16px",
    color: "#e8f5f5", fontSize: "14px", outline: "none",
    width: "100%", boxSizing: "border-box",
  },
  passwordRow: { position: "relative" },
  eyeBtn: {
    position: "absolute", right: "12px", top: "50%",
    transform: "translateY(-50%)",
    background: "none", border: "none", cursor: "pointer",
    padding: "4px", display: "flex", alignItems: "center",
  },
  submitBtn: {
    background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
    color: "#0a0f0f", border: "none", borderRadius: "10px",
    padding: "14px", fontSize: "15px", fontWeight: "700",
    cursor: "pointer", fontFamily: "inherit", marginTop: "8px",
  },
  link: {
    display: "block", textAlign: "center", marginTop: "16px",
    color: "#2ec4b6", textDecoration: "none", fontWeight: "600",
  },
};




